/**
 * Resolve custom URL paths into concrete sequence IDs.
 * Used as a local/dev fallback when server-side rewrites are not available.
 */

const RESERVED_SINGLE_SEGMENT_PATHS = new Set([
  'dashboard',
  'discover',
  'blog',
  'docs',
  'newsletter',
  'admin',
  'api',
  'demo',
  'viewer.html',
  'index.html',
  'favicon.ico',
  'manifest.json',
  'robots.txt',
  'sitemap.xml',
]);

export interface PathResolverDeps {
  resolveGlobalSlug(slug: string): Promise<string | null>;
  resolveUsernameToUserId(username: string): Promise<string | null>;
  resolveUserSlug(userId: string, slug: string): Promise<string | null>;
}

/**
 * Resolve sequenceId for:
 * - /{globalSlug}
 * - /{username}/{userSlug}
 *
 * Returns null for non-viewer routes.
 */
export async function resolveSequenceIdFromPathname(
  pathname: string,
  deps: PathResolverDeps
): Promise<string | null> {
  const segments = pathname
    .split('/')
    .map((s) => decodeURIComponent(s.trim().toLowerCase()))
    .filter(Boolean);

  if (segments.length === 1) {
    const [single] = segments;
    if (!single || RESERVED_SINGLE_SEGMENT_PATHS.has(single)) {
      return null;
    }
    return deps.resolveGlobalSlug(single);
  }

  if (segments.length === 2) {
    const [username, slug] = segments;
    if (!username || !slug) {
      return null;
    }

    const userId = await deps.resolveUsernameToUserId(username);
    if (!userId) {
      return null;
    }

    return deps.resolveUserSlug(userId, slug);
  }

  return null;
}

